<div class="account-settings-page">
  <header class="account-settings-page__title-card page-card card">
    <div class="card-body">
      <h1 class="account-settings-page__title bw-title-with-info">
        Addresses
        <app-info-tooltip compact placement="right" text="Save shipping, billing, and other addresses as reusable cards. You can choose a default address for each kind."></app-info-tooltip>
      </h1>
    </div>
  </header>

  @if (isInitialLoading) {
    <div class="loading-wrapper">
      <ion-spinner name="crescent"></ion-spinner>
      <p>Loading addresses...</p>
    </div>
  } @else if (hasInitialLoadError) {
    <app-api-load-error
      title="Could not load addresses"
      message="Some error occurred while loading your saved addresses. Please try again."
      retryLabel="Retry"
      (retry)="loadAddresses()"
    ></app-api-load-error>
  } @else {
    <section class="page-card card">
      <div class="card-body">
        @if (addressMessage) {
          <div class="alert alert-success">{{ addressMessage }}</div>
        }
        @if (addressError) {
          <div class="alert alert-danger">{{ addressError }}</div>
        }

        <form #newAddressFormElement class="mb-4" (ngSubmit)="createAddress(newAddressFormElement)">
          <h2 class="section-title mb-3">Add New Address</h2>
          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label" for="new_address_kind">Kind</label>
              <select id="new_address_kind" class="form-select" [(ngModel)]="newAddressForm.kind" name="new_address_kind" required>
                <option value="" disabled hidden>Select address kind</option>
                <option value="shipping">Shipping</option>
                <option value="billing">Billing</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">Label</label>
              <input
                type="text"
                class="form-control"
                [(ngModel)]="newAddressForm.label"
                name="new_address_label"
                placeholder="Head Office"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label">Full Name</label>
              <input
                type="text"
                class="form-control"
                [(ngModel)]="newAddressForm.fullName"
                name="new_address_full_name"
                placeholder="John Doe"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label" for="new_address_line1">Line 1</label>
              <input
                id="new_address_line1"
                type="text"
                class="form-control"
                [(ngModel)]="newAddressForm.line1"
                name="new_address_line1"
                required
              />
            </div>
            <div class="col-md-6">
              <label class="form-label">Line 2</label>
              <input
                type="text"
                class="form-control"
                [(ngModel)]="newAddressForm.line2"
                name="new_address_line2"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label" for="new_address_city">City</label>
              <input
                id="new_address_city"
                type="text"
                class="form-control"
                [(ngModel)]="newAddressForm.city"
                name="new_address_city"
                required
              />
            </div>
            <div class="col-md-4">
              <label class="form-label">State</label>
              <input
                type="text"
                class="form-control"
                [(ngModel)]="newAddressForm.state"
                name="new_address_state"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label" for="new_address_postal_code">Postal Code</label>
              <input
                id="new_address_postal_code"
                type="text"
                class="form-control"
                [(ngModel)]="newAddressForm.postalCode"
                name="new_address_postal_code"
                required
              />
            </div>
            <div class="col-md-4">
              <label class="form-label" for="new_address_country">Country</label>
              <input
                id="new_address_country"
                type="text"
                class="form-control"
                [(ngModel)]="newAddressForm.country"
                name="new_address_country"
                required
              />
            </div>
            <div class="col-md-4">
              <label class="form-label">Phone</label>
              <input
                type="text"
                class="form-control"
                [(ngModel)]="newAddressForm.phone"
                name="new_address_phone"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label">Company</label>
              <input
                type="text"
                class="form-control"
                [(ngModel)]="newAddressForm.company"
                name="new_address_company"
              />
            </div>
          </div>

          <div class="d-flex align-items-center justify-content-between mt-3">
            <div class="form-check">
              <input
                type="checkbox"
                class="form-check-input"
                [(ngModel)]="newAddressForm.isDefault"
                name="new_address_default"
                id="new_address_default"
              />
              <label class="form-check-label" for="new_address_default">Set as default for this type</label>
            </div>
            <button class="btn btn-outline-primary" type="submit" [disabled]="isAddressBusy">
              Add Address
            </button>
          </div>
        </form>

        @if (addresses.length === 0) {
          <div class="empty-state">
            No saved addresses yet.
          </div>
        }

        @if (addresses.length > 0) {
          <div class="address-grid">
            @for (address of addresses; track address.publicId) {
              <article class="address-card">
                <div class="address-header">
                  <span class="badge" [class.billing]="address.kind === 'billing'">
                    {{ kindLabel(address.kind) }}
                  </span>
                  @if (address.isDefault) {
                    <span class="default-tag">Default</span>
                  }
                </div>
                <h4 class="h6 mb-1">{{ address.label || address.fullName || 'Address Card' }}</h4>
                <p class="mb-1 text-muted">
                  {{ address.line1 }}@if (address.line2) {
                    <span>, {{ address.line2 }}</span>
                  }
                </p>
                <p class="mb-1 text-muted">{{ address.city }}, {{ address.state || '-' }} {{ address.postalCode }}</p>
                <p class="mb-2 text-muted">{{ address.country }}</p>
                <div class="card-actions mb-2">
                  <button class="btn btn-sm btn-outline-secondary" type="button" (click)="startEditAddress(address)">
                    Edit
                  </button>
                  <button class="btn btn-sm btn-outline-danger" type="button" (click)="deleteAddress(address.publicId)">
                    Delete
                  </button>
                </div>

                @if (editingAddressPublicId === address.publicId) {
                  <form #editAddressFormElement (ngSubmit)="saveAddressEdit(editAddressFormElement)">
                    <div class="row g-2">
                      <div class="col-6">
                        <label class="form-label small" [for]="'edit_kind_' + address.publicId">Kind</label>
                        <select
                          [id]="'edit_kind_' + address.publicId"
                          class="form-select form-select-sm"
                          [(ngModel)]="editAddressForm.kind"
                          [name]="'edit_kind_' + address.publicId"
                          required
                        >
                          <option value="shipping">Shipping</option>
                          <option value="billing">Billing</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div class="col-6">
                        <label class="form-label small">Label</label>
                        <input
                          class="form-control form-control-sm"
                          [(ngModel)]="editAddressForm.label"
                          [name]="'edit_label_' + address.publicId"
                        />
                      </div>
                      <div class="col-12">
                        <label class="form-label small" [for]="'edit_line1_' + address.publicId">Line 1</label>
                        <input
                          [id]="'edit_line1_' + address.publicId"
                          class="form-control form-control-sm"
                          [(ngModel)]="editAddressForm.line1"
                          [name]="'edit_line1_' + address.publicId"
                          required
                        />
                      </div>
                      <div class="col-12">
                        <label class="form-label small">Line 2</label>
                        <input
                          class="form-control form-control-sm"
                          [(ngModel)]="editAddressForm.line2"
                          [name]="'edit_line2_' + address.publicId"
                        />
                      </div>
                      <div class="col-6">
                        <label class="form-label small" [for]="'edit_city_' + address.publicId">City</label>
                        <input
                          [id]="'edit_city_' + address.publicId"
                          class="form-control form-control-sm"
                          [(ngModel)]="editAddressForm.city"
                          [name]="'edit_city_' + address.publicId"
                          required
                        />
                      </div>
                      <div class="col-6">
                        <label class="form-label small">State</label>
                        <input
                          class="form-control form-control-sm"
                          [(ngModel)]="editAddressForm.state"
                          [name]="'edit_state_' + address.publicId"
                        />
                      </div>
                      <div class="col-6">
                        <label class="form-label small" [for]="'edit_postal_' + address.publicId">Postal Code</label>
                        <input
                          [id]="'edit_postal_' + address.publicId"
                          class="form-control form-control-sm"
                          [(ngModel)]="editAddressForm.postalCode"
                          [name]="'edit_postal_' + address.publicId"
                          required
                        />
                      </div>
                      <div class="col-6">
                        <label class="form-label small" [for]="'edit_country_' + address.publicId">Country</label>
                        <input
                          [id]="'edit_country_' + address.publicId"
                          class="form-control form-control-sm"
                          [(ngModel)]="editAddressForm.country"
                          [name]="'edit_country_' + address.publicId"
                          required
                        />
                      </div>
                      <div class="col-6">
                        <label class="form-label small">Phone</label>
                        <input
                          class="form-control form-control-sm"
                          [(ngModel)]="editAddressForm.phone"
                          [name]="'edit_phone_' + address.publicId"
                        />
                      </div>
                      <div class="col-6">
                        <label class="form-label small">Company</label>
                        <input
                          class="form-control form-control-sm"
                          [(ngModel)]="editAddressForm.company"
                          [name]="'edit_company_' + address.publicId"
                        />
                      </div>
                      <div class="col-12">
                        <div class="form-check mt-1">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            [(ngModel)]="editAddressForm.isDefault"
                            [name]="'edit_default_' + address.publicId"
                            [id]="'edit_default_' + address.publicId"
                          />
                          <label class="form-check-label small" [for]="'edit_default_' + address.publicId">
                            Set as default for this type
                          </label>
                        </div>
                      </div>
                    </div>

                    <div class="d-flex gap-2 mt-2">
                      <button class="btn btn-sm btn-primary" type="submit" [disabled]="isAddressBusy">Save</button>
                      <button class="btn btn-sm btn-outline-secondary" type="button" (click)="cancelEditAddress()">
                        Cancel
                      </button>
                    </div>
                  </form>
                }
              </article>
            }
          </div>
        }
      </div>
    </section>
  }
</div>
